import { useCameras } from "@src/services/api/hooks";
import type { CameraApiResponse } from "@src/services/api/cameras-api";

export type CamerasListState =
  | { status: "pending" }
  | { status: "error"; error: Error; refetch: () => void }
  | { status: "success"; cameras: CameraApiResponse[]; refetch: () => void };

function toError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

/**
 * Derives a single UI phase for tenant-scoped camera lists so consumers never treat
 * undefined query data as an empty list during tenant resolution or the first fetch
 * (SATD-397). Prefer switching on {@link CamerasListState.status} instead of open-coded
 * `isLoading` / `isFetching` / `isTenantPending` checks in each screen.
 */
export function useCamerasListState(siteId?: string): CamerasListState {
  const { data: cameras, isLoading, isFetching, error, isTenantPending, refetch } = useCameras(siteId);

  const isPending = isLoading || isTenantPending || (isFetching && cameras === undefined);

  if (isPending) {
    return { status: "pending" };
  }

  if (error) {
    return { status: "error", error: toError(error), refetch };
  }

  return { status: "success", cameras: cameras ?? [], refetch };
}
